import { pickPalette } from "@picnic-field/renderer/appearance";
import type { PaletteLike } from "@picnic-field/renderer/appearance";
import type { FeCode } from "../../../lib/http/errors";
import type { BenchSource, CampusSource } from "../../../lib/api/fieldApi";
import { Button } from "../../../components/ui/Button";
import { FeBanner } from "../../../components/FeBanner";
import { ModelPreview } from "../../../components/ModelPreview";
import { PicoBubble } from "../../../components/PicoBubble";
import { TagChips } from "../../../components/ui/TagChips";
import { useI18n } from "../../../i18n";
import { STATIC_TEXT, benchUrl } from "../../../lib/staticReason";
import type { StaticReason } from "../../../lib/staticReason";
import {
  STAGES,
  firstError,
  isStaticSelection,
  stageViews,
  staticSelectionReason,
  studentHints,
} from "../lib/checks";
import { modelName, workTitle } from "../lib/titles";
import type { CheckState, StageState } from "../types";

export interface CheckPaneProps {
  /** null = 아직 고르지 않음(오른쪽 페인은 비어 있는 확인 틀 — 와이어 좌: 선택 · 우: 검증·확정) */
  imp: CampusSource | null;
  project: BenchSource | null;
  state: CheckState;
  palette: PaletteLike | null;
  /** 서버 별명(남에게 보이는 이름) — 모르면 "" (작품명은 별명 없이) */
  displayName: string;
  saving: boolean;
  onBack: () => void;
  onRetry: () => void;
  onConfirm: () => void;
  /**
   * 달릴 수 있는 SPIKE 프로그램(저장본 있음)이 있으면 — 정적 안내의 '🧩 Bench 프로그램 고르기'(고르기로 돌아가 그 프로그램을 고른다).
   * 없으면 버튼 대신 문구(Bench 주소가 설정돼 있으면 '🧩 Bench 로 가기' 새 탭).
   */
  onPickProgram?: (() => void) | null;
  /**
   * Sprint 4 'Bench 에서 고치기'(SR-INT-02) — 고른 Bench 프로젝트를 입장권으로 연다(돌아오면 그 저장본으로 다시 확인).
   * 고른 프로젝트가 있고 Bench 가 설정돼 있을 때만. 없으면(프로그램 없음) Bench 첫 화면 새 탭(예전 그대로).
   */
  onFixInBench?: (() => void) | null;
}

const MARK: Record<StageState, string> = {
  checking: "…",
  done: "✓",
  hint: "💡",
  fail: "✖",
  unchecked: "·",
  skip: "–",
};

/**
 * FS-02-02 작품 올리기 — 검증·확정.
 *  - 검증 단계(V-01~06 → 모양·부품·연결·기어·포트·프로그램), 첫 실패에서 멈춤(SR-CMP-01).
 *  - 3D 미리보기: Campus 에서 칠한 색, 돌려 보기만 — 색 편집 버튼 없음(SR-MDF-06). 팔레트가 없으면 원래 색(오류 아님).
 *  - 실패: 피코 문구 + 학생 배너(코드 없음). 경고(FE-103 등)는 막지 않는 힌트.
 *  - 작품명은 자동(학생 편집 불가 · SR-SEC-02).
 */
export function CheckPane(p: CheckPaneProps) {
  const { t, locale } = useI18n();
  const res = p.state.status === "result" ? p.state.result : null;
  const issues = res?.issues ?? null;
  const error: FeCode | null = res
    ? ((p.state.status === "result" ? p.state.rejected : undefined) ??
      firstError(res.issues) ??
      (res.ok ? null : "FE-101"))
    : null;
  const ok = !!res && res.ok && !error;
  const hints = issues ? studentHints(issues) : [];
  const staticWork = res?.static ?? isStaticSelection(p.project);
  // 이유는 고른 것에서 — 프로그램 없음 · micro:bit · (SPIKE 인데 정적 =) 구동 바퀴에 모터가 닿지 않음
  const reason = staticSelectionReason(p.project);
  const idle = p.state.status === "idle" || !p.imp;
  const stages = idle
    ? STAGES.map((id) => ({ id, state: "unchecked" as StageState }))
    : stageViews(p.state.status === "checking" ? null : (issues ?? []), !!p.project);
  const appearance = res?.preview?.appearance ?? p.imp?.appearance ?? null;
  const tinted =
    !!pickPalette(p.palette, appearance?.palette) &&
    !!appearance &&
    Object.keys(appearance.colors ?? {}).length > 0;
  const gearOk = !!res?.tags.includes("gear-ratio") && !hints.length;

  const pico = idle
    ? t("student.upload.pico.pick")
    : p.state.status === "checking"
      ? t("student.upload.pico.checking")
      : ok
        ? staticWork
          ? t(STATIC_TEXT[reason].pico)
          : hints.length
            ? t("student.upload.pico.okHint")
            : gearOk && tinted
              ? t("student.upload.pico.okGear")
              : t("student.upload.pico.ok")
        : t("student.upload.pico.fail");

  const name = p.imp ? modelName(t, locale, p.imp.modelSlug, p.imp.title) : "";
  const title = p.imp ? workTitle(t, locale, p.imp, p.displayName) : "";
  const failIssue = error ? res?.issues.find((i) => i.severity === "error") : undefined;
  const failDetail = failIssue?.path?.trim() || true;

  return (
    <div
      className="pf-upload__check"
      aria-busy={p.state.status === "checking" || p.saving}
      data-state={idle ? "idle" : p.state.status}
    >
      <PicoBubble text={pico} busy={p.state.status === "checking"} live={!idle} />

      <ol className="pf-stages" aria-label={t("student.upload.stages.label")}>
        {stages.map((s) => {
          const label = t(`student.upload.stages.${s.id}`);
          return (
            <li
              key={s.id}
              className={`pf-stage pf-stage--${s.state}`}
              data-stage={s.id}
              data-state={s.state}
              aria-label={t(`student.upload.stageState.${s.state}`, { label })}
            >
              <span aria-hidden="true">
                {label} {MARK[s.state]}
              </span>
            </li>
          );
        })}
      </ol>

      {idle ? (
        <div className="pf-upload__work">
          <div className="pf-upload__preview pf-machine__thumb" aria-hidden="true" />
          <div className="pf-upload__meta">
            <p className="pf-muted">{t("student.upload.autoName")}</p>
            <p className="pf-muted pf-upload__colors">
              <span aria-hidden="true">🎨 </span>
              {t("student.upload.colorsFrom")} · {t("student.upload.colorsNote")}
            </p>
          </div>
        </div>
      ) : (
        <div className="pf-upload__work">
          <ModelPreview
            className="pf-upload__preview"
            glbUrl={res?.preview?.glbUrl || null}
            appearance={appearance}
            palette={p.palette}
            label={t("student.upload.previewLabel", { title: name })}
            overlay={
              <span className="pf-preview__hint" aria-hidden="true">
                3D ⟲ {t("student.upload.rotateHint")}
              </span>
            }
            fallback={
              ok && (
                <p className="pf-muted pf-preview__note">{t("student.upload.previewFallback")}</p>
              )
            }
          />
          <div className="pf-upload__meta">
            <p className="pf-work__title" data-testid="auto-title">
              {title}
            </p>
            <p className="pf-muted">{t("student.upload.autoName")}</p>
            <p className="pf-muted pf-upload__colors" data-testid="colors-note">
              <span aria-hidden="true">🎨 </span>
              {tinted ? (
                <>
                  {t("student.upload.colorsFrom")} · {t("student.upload.colorsNote")}
                </>
              ) : (
                <>
                  {t("student.upload.colorsOriginal")} · {t("student.upload.colorsNote")}
                </>
              )}
            </p>
            {staticWork && (
              <ul className="pf-chips">
                <li className="pf-chip-static pf-chip-static--run" data-testid="static-chip">
                  {t("student.upload.staticChip")}
                </li>
              </ul>
            )}
            {ok && res && <TagChips tags={res.tags} label={t("student.work.tags")} />}
          </div>
        </div>
      )}

      {ok && staticWork && (
        <StaticGuide
          reason={reason}
          onPickProgram={p.onPickProgram ?? null}
          onFixInBench={p.project ? (p.onFixInBench ?? null) : null}
          bench={benchUrl()}
        />
      )}
      {error && (
        // 막는 실패는 err(✖ · role=alert — 와이어 FS-02-02). FE-102 는 '새로 받는 중' 안내라 기본(info)
        <FeBanner
          code={error}
          audience="student"
          severity={error === "FE-102" ? undefined : "err"}
          icon={error === "FE-102" ? undefined : "✖"}
          teacherFold={failDetail}
        />
      )}
      {ok &&
        hints.map((code) => (
          <div key={code} className="pf-hint" data-hint={code}>
            <FeBanner code={code} audience="student" severity="warn" />
          </div>
        ))}
      {p.state.status === "error" && (
        <FeBanner
          code={p.state.code}
          audience="student"
          actions={[{ label: t("common.retry"), icon: "↻", onClick: p.onRetry }]}
        />
      )}
      {error === "FE-102" && (
        <div className="pf-upload__actions pf-upload__actions--start">
          <Button variant="ghost" icon="↻" onClick={p.onRetry}>
            {t("common.retry")}
          </Button>
        </div>
      )}

      <div className="pf-upload__confirm">
        <Button
          variant="ghost"
          icon="↩"
          onClick={p.onBack}
          disabled={p.saving || idle}
          className="pf-upload__back"
        >
          {t("student.upload.back")}
        </Button>
        <Button
          variant="accent"
          className="pf-upload__go"
          icon="⬆"
          disabled={!ok || p.saving}
          aria-busy={p.saving || undefined}
          onClick={p.onConfirm}
          data-testid="confirm-upload"
        >
          {p.saving ? t("student.upload.uploading") : t("student.upload.confirm")}
        </Button>
      </div>
    </div>
  );
}

/**
 * 정적 전시 안내(FS-02-02) — 왜 움직이지 않는지(이유 한 줄) + 무엇을 하면 달리는지(다음 행동).
 * 배너 틀(pf-banner--info)을 그대로 쓴다. 버튼은 SE 64px(pf-btn 공통 토큰).
 */
function StaticGuide({
  reason,
  onPickProgram,
  onFixInBench,
  bench,
}: {
  reason: StaticReason;
  onPickProgram: (() => void) | null;
  /** 고른 프로젝트를 입장권으로(SPIKE 인데 바퀴 · 모터가 어긋남 — 프로그램의 모터 포트를 고쳐 볼 수 있다) */
  onFixInBench: (() => void) | null;
  bench: string | null;
}) {
  const { t } = useI18n();
  const text = STATIC_TEXT[reason];
  const pick = reason !== "wheels" && onPickProgram;
  // micro:bit 은 고쳐도 달리지 않는다(SPIKE 를 골라야) — 입장권은 SPIKE(바퀴 판정)일 때만
  const fix = !pick && reason === "wheels" && onFixInBench;
  const toBench = !pick && !fix && reason === "noProgram" && bench;
  return (
    <div
      className="pf-banner pf-banner--info"
      role="note"
      aria-label={t("student.static.label")}
      data-testid="static-guide"
      data-reason={reason}
    >
      <div className="pf-banner__body">
        <span className="pf-banner__icon" aria-hidden="true">
          {reason === "wheels" ? "🔧" : "🖼"}
        </span>
        <div className="pf-banner__text">
          <b className="pf-banner__cause">{t(text.why)}</b>
          <span className="pf-banner__next"> {t(text.next)}</span>
        </div>
      </div>
      {(pick || fix || toBench) && (
        <div className="pf-banner__actions">
          {fix ? (
            <Button
              variant="ghost"
              icon="🧩"
              onClick={onFixInBench}
              data-testid="static-fix-in-bench"
            >
              {t("student.static.fixInBench")}
            </Button>
          ) : pick ? (
            <Button
              variant="ghost"
              icon="🧩"
              onClick={onPickProgram}
              data-testid="static-pick-program"
            >
              {t("student.static.pickProgram")}
            </Button>
          ) : (
            <Button
              variant="ghost"
              icon="🧩"
              onClick={() => window.open(bench as string, "_blank", "noopener")}
              data-testid="static-to-bench"
            >
              {t("student.static.toBench")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
